import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, ShieldCheck, ShieldAlert, Cpu, Server, Monitor,
    Laptop, Users, RefreshCw, Search, CheckCircle2, AlertTriangle,
    ExternalLink, Lock, Eye, Key, Radio, Layers, Activity,
    FileText, ArrowUpRight, ChevronRight, X, Clock, Sliders, Check,
    LayoutGrid, List, AlertCircle, HelpCircle
} from "lucide-react";

export default function MicrosoftDefender() {
    const [status, setStatus] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [machines, setMachines] = useState([]);
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("alerts"); // 'alerts', 'machines', 'incidents', 'actions'
    const [selectedItem, setSelectedItem] = useState(null);
    const [lastSync, setLastSync] = useState(null);

    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const fetchData = async (force = false) => {
        if (force) setRefreshing(true);
        else setLoading(true);

        try {
            const [statusRes, alertsRes, machinesRes, incidentsRes] = await Promise.all([
                axios.get(`${backendURL}/api/soc/integrations/defender/status`, { headers }),
                axios.get(`${backendURL}/api/soc/integrations/defender/alerts`, { headers }),
                axios.get(`${backendURL}/api/soc/integrations/defender/machines`, { headers }),
                axios.get(`${backendURL}/api/soc/integrations/defender/incidents`, { headers })
            ]);

            setStatus(statusRes.data);
            setAlerts(alertsRes.data.alerts || []);
            setMachines(machinesRes.data.machines || []);
            setIncidents(incidentsRes.data.incidents || []);
            setLastSync(new Date());
        } catch (err) {
            console.error("Failed to load Microsoft Defender telemetry:", err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
        const timer = setInterval(() => fetchData(false), 120000); // 2 min auto refresh
        return () => clearInterval(timer);
    }, []);

    // Filter alerts
    const filteredAlerts = useMemo(() => {
        if (!searchQuery.trim()) return alerts;
        const q = searchQuery.toLowerCase();
        return alerts.filter(a =>
            (a.title || "").toLowerCase().includes(q) ||
            (a.deviceName || "").toLowerCase().includes(q) ||
            (a.category || "").toLowerCase().includes(q) ||
            (a.severity || "").toLowerCase().includes(q) ||
            (a.mitreTechniques || []).some(t => t.toLowerCase().includes(q))
        );
    }, [alerts, searchQuery]);

    // Filter machines
    const filteredMachines = useMemo(() => {
        if (!searchQuery.trim()) return machines;
        const q = searchQuery.toLowerCase();
        return machines.filter(m =>
            (m.computerDnsName || "").toLowerCase().includes(q) ||
            (m.osPlatform || "").toLowerCase().includes(q) ||
            (m.healthStatus || "").toLowerCase().includes(q) ||
            (m.riskScore || "").toLowerCase().includes(q)
        );
    }, [machines, searchQuery]);

    return (
        <div className="w-full space-y-5 text-slate-200 pb-12">
            {/* ── Top Header & Integration Banner (Full Width) ── */}
            <div className="w-full flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-900/80 border border-white/[0.08] p-6 rounded-2xl backdrop-blur-xl relative overflow-hidden shadow-2xl">
                <div className="absolute -right-20 -top-20 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-700 to-sky-600 flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
                        <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-xl font-bold tracking-tight text-white">Microsoft Defender XDR</h1>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-sky-500/10 text-sky-400 border border-sky-500/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
                                Defender for Endpoint
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                <Check className="w-3 h-3" /> Strict Read-Only Mode
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono text-slate-400 bg-white/[0.03] border border-white/[0.06]">
                                Tenant: {status?.tenantId ? `${status.tenantId.slice(0, 8)}...` : "Azure AD"}
                            </span>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">
                            Enterprise endpoint threat telemetry, security alerts, and machine health correlated with FortiGate edge sensors.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <div className="px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-xs text-slate-400 flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-slate-500" />
                        <span>Synced: {lastSync ? lastSync.toLocaleTimeString() : "Pending"}</span>
                    </div>

                    <button
                        onClick={() => fetchData(true)}
                        disabled={refreshing || loading}
                        className="flex items-center gap-2 px-3.5 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                        <span>{refreshing ? "Syncing..." : "Sync Defender"}</span>
                    </button>
                </div>
            </div>

            {/* ── Status / Diagnostic Alert ── */}
            {status?.status === "AWAITING_AZURE_ADMIN_CONSENT" ? (
                <div className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-amber-950/40 border border-amber-500/30 text-xs">
                    <div className="flex items-start gap-3 text-amber-200">
                        <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                            <div className="font-semibold text-amber-300">Azure AD OAuth2 Token Connected • Awaiting API Permissions Consent</div>
                            <p className="text-amber-300/80 mt-0.5">
                                Token successfully acquired from Azure AD. To display live Defender alerts & machine inventory, click <strong>"Grant admin consent"</strong> in Azure Portal for the registered application.
                            </p>
                        </div>
                    </div>
                    <a
                        href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
                        target="_blank"
                        rel="noreferrer"
                        className="px-3.5 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors inline-flex items-center gap-1.5"
                    >
                        Open Azure Portal
                    </a>
                </div>
            ) : (
                <div className="w-full flex items-center justify-between gap-4 px-4 py-3 rounded-xl bg-blue-950/40 border border-blue-500/20 text-xs">
                    <div className="flex items-center gap-2.5 text-blue-300">
                        <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        <span>
                            <strong>Operational Mode:</strong> Ingesting Microsoft Defender alerts, machine inventory, and incident stories in <strong>Strict Read-Only Mode</strong>.
                        </span>
                    </div>
                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                        STATUS: OPERATIONAL
                    </span>
                </div>
            )}

            {/* ── Metric KPI Cards ── */}
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-4 gap-4">
                <div className="p-5 rounded-xl bg-slate-900/60 border border-white/[0.06] backdrop-blur-sm">
                    <div className="flex items-center justify-between text-slate-400 mb-2">
                        <span className="text-xs font-medium uppercase tracking-wider">Security Alerts</span>
                        <ShieldAlert className="w-4 h-4 text-rose-400" />
                    </div>
                    <div className="text-2xl font-bold text-white">{alerts.length}</div>
                    <p className="text-[11px] text-slate-500 mt-1">Live Endpoint Detections</p>
                </div>

                <div className="p-5 rounded-xl bg-slate-900/60 border border-white/[0.06] backdrop-blur-sm">
                    <div className="flex items-center justify-between text-slate-400 mb-2">
                        <span className="text-xs font-medium uppercase tracking-wider">Onboarded Machines</span>
                        <Monitor className="w-4 h-4 text-sky-400" />
                    </div>
                    <div className="text-2xl font-bold text-sky-400">{machines.length}</div>
                    <p className="text-[11px] text-slate-500 mt-1">Protected Windows / Server Fleet</p>
                </div>

                <div className="p-5 rounded-xl bg-slate-900/60 border border-white/[0.06] backdrop-blur-sm">
                    <div className="flex items-center justify-between text-slate-400 mb-2">
                        <span className="text-xs font-medium uppercase tracking-wider">XDR Incidents</span>
                        <Layers className="w-4 h-4 text-purple-400" />
                    </div>
                    <div className="text-2xl font-bold text-purple-400">{incidents.length}</div>
                    <p className="text-[11px] text-slate-500 mt-1">Correlated Attack Stories</p>
                </div>

                <div className="p-5 rounded-xl bg-slate-900/60 border border-white/[0.06] backdrop-blur-sm">
                    <div className="flex items-center justify-between text-slate-400 mb-2">
                        <span className="text-xs font-medium uppercase tracking-wider">Connection Status</span>
                        <Key className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div className="text-2xl font-bold text-emerald-400">Connected</div>
                    <p className="text-[11px] text-slate-500 mt-1">Azure AD Client Credentials</p>
                </div>
            </div>

            {/* ── Navigation Tabs & Search ── */}
            <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-white/[0.06] pb-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={() => setActiveTab("alerts")}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            activeTab === "alerts"
                                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                                : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                        }`}
                    >
                        Security Alerts ({alerts.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("machines")}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            activeTab === "machines"
                                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                                : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                        }`}
                    >
                        Onboarded Machines ({machines.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("incidents")}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            activeTab === "incidents"
                                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                                : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                        }`}
                    >
                        XDR Incidents ({incidents.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("actions")}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            activeTab === "actions"
                                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                                : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                        }`}
                    >
                        Read-Only SOAR Actions
                    </button>
                </div>

                {(activeTab === "alerts" || activeTab === "machines") && (
                    <div className="relative w-full sm:w-80">
                        <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder={activeTab === "alerts" ? "Filter alerts, techniques, devices..." : "Filter machines, OS, status..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-900/60 border border-white/[0.08] rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50"
                        />
                    </div>
                )}
            </div>

            {/* ── TAB CONTENT ── */}
            {loading ? (
                <div className="py-24 flex flex-col items-center justify-center text-slate-500 gap-3">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                    <p className="text-xs">Querying Microsoft Graph & Defender APIs...</p>
                </div>
            ) : (
                <>
                    {/* 1️⃣ Security Alerts View */}
                    {activeTab === "alerts" && (
                        filteredAlerts.length > 0 ? (
                            <div className="w-full rounded-xl bg-slate-900/70 border border-white/[0.06] overflow-x-auto shadow-xl">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-white/[0.02] border-b border-white/[0.06] text-slate-400">
                                        <tr>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Severity</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Alert Title</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Category</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Affected Host / Device</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">MITRE Techniques</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Detection Time</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px] text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/[0.04] text-slate-300">
                                        {filteredAlerts.map((alert) => (
                                            <tr key={alert.id} className="hover:bg-white/[0.02] transition-colors">
                                                <td className="py-3 px-4">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                                        alert.severity === "high" ? "bg-rose-500/10 text-rose-400 border border-rose-500/20" :
                                                        alert.severity === "medium" ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" :
                                                        alert.severity === "low" ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" :
                                                        "bg-slate-500/10 text-slate-400 border border-slate-500/20"
                                                    }`}>
                                                        {alert.severity}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 font-semibold text-white max-w-xs truncate">
                                                    {alert.title}
                                                </td>
                                                <td className="py-3 px-4 text-slate-400">{alert.category}</td>
                                                <td className="py-3 px-4 font-mono text-[11px] text-sky-400">{alert.deviceName}</td>
                                                <td className="py-3 px-4">
                                                    <div className="flex flex-wrap gap-1">
                                                        {(alert.mitreTechniques || []).slice(0, 2).map((t, idx) => (
                                                            <span key={idx} className="px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 font-mono text-[10px] border border-purple-500/20">
                                                                {t}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 font-mono text-[11px] text-slate-500">
                                                    {alert.createdDateTime ? new Date(alert.createdDateTime).toLocaleString() : "Recent"}
                                                </td>
                                                <td className="py-3 px-4 text-right">
                                                    <button
                                                        onClick={() => setSelectedItem({ type: "alert", data: alert })}
                                                        className="px-2.5 py-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 rounded text-xs font-semibold transition-colors"
                                                    >
                                                        Inspect
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-12 rounded-xl bg-slate-900/40 border border-white/[0.06] text-center space-y-3">
                                <ShieldCheck className="w-10 h-10 text-slate-600 mx-auto" />
                                <div className="text-white font-semibold">No Security Alerts Reported</div>
                                <p className="text-xs text-slate-500 max-w-md mx-auto">
                                    {status?.status === "AWAITING_AZURE_ADMIN_CONSENT"
                                        ? "Azure OAuth2 token is active. Once Admin Consent is granted for 'SecurityAlert.Read.All', live alerts will automatically populate here."
                                        : "0 active alerts reported by Microsoft Defender in the current query window."}
                                </p>
                            </div>
                        )
                    )}

                    {/* 2️⃣ Onboarded Machines View */}
                    {activeTab === "machines" && (
                        filteredMachines.length > 0 ? (
                            <div className="w-full rounded-xl bg-slate-900/70 border border-white/[0.06] overflow-x-auto shadow-xl">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-white/[0.02] border-b border-white/[0.06] text-slate-400">
                                        <tr>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Computer Name</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Platform / OS</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Health Status</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Risk Score</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Exposure Level</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Last Seen</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/[0.04] text-slate-300">
                                        {filteredMachines.map((m) => (
                                            <tr key={m.id} className="hover:bg-white/[0.02] transition-colors">
                                                <td className="py-3 px-4 font-medium text-white font-mono flex items-center gap-2">
                                                    <Monitor className="w-4 h-4 text-sky-400 flex-shrink-0" />
                                                    <span>{m.computerDnsName}</span>
                                                </td>
                                                <td className="py-3 px-4 text-slate-400">{m.osPlatform} {m.osVersion}</td>
                                                <td className="py-3 px-4">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                                        m.healthStatus === "Active" ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-500/10 text-slate-400"
                                                    }`}>
                                                        {m.healthStatus}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 font-semibold text-slate-300">{m.riskScore}</td>
                                                <td className="py-3 px-4 text-slate-400">{m.exposureLevel}</td>
                                                <td className="py-3 px-4 font-mono text-[11px] text-slate-500">
                                                    {m.lastSeen ? new Date(m.lastSeen).toLocaleString() : "Recently"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-12 rounded-xl bg-slate-900/40 border border-white/[0.06] text-center space-y-3">
                                <Monitor className="w-10 h-10 text-slate-600 mx-auto" />
                                <div className="text-white font-semibold">No Machines Discovered Yet</div>
                                <p className="text-xs text-slate-500 max-w-md mx-auto">
                                    {status?.status === "AWAITING_AZURE_ADMIN_CONSENT"
                                        ? "Awaiting 'Machine.Read.All' Admin Consent in Azure AD to query onboarded endpoints."
                                        : "Defender for Endpoint machine inventory is currently empty or syncing."}
                                </p>
                            </div>
                        )
                    )}

                    {/* 3️⃣ XDR Incidents View */}
                    {activeTab === "incidents" && (
                        incidents.length > 0 ? (
                            <div className="w-full rounded-xl bg-slate-900/70 border border-white/[0.06] overflow-x-auto shadow-xl">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-white/[0.02] border-b border-white/[0.06] text-slate-400">
                                        <tr>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Severity</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Incident ID</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Incident Name</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Status</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Alerts Count</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Created Time</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px] text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/[0.04] text-slate-300">
                                        {incidents.map((inc) => (
                                            <tr key={inc.id} className="hover:bg-white/[0.02] transition-colors">
                                                <td className="py-3 px-4">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                                        inc.severity === "high" ? "bg-rose-500/10 text-rose-400 border border-rose-500/20" :
                                                        inc.severity === "medium" ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" :
                                                        inc.severity === "low" ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" :
                                                        "bg-slate-500/10 text-slate-400 border border-slate-500/20"
                                                    }`}>
                                                        {inc.severity}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 font-mono text-[11px] text-purple-400">#{inc.id}</td>
                                                <td className="py-3 px-4 font-semibold text-white max-w-sm truncate">{inc.displayName}</td>
                                                <td className="py-3 px-4">
                                                    <span className="px-2 py-0.5 rounded text-[10px] bg-white/[0.04] text-slate-300 capitalize">
                                                        {inc.status}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 font-mono text-slate-400">{inc.alertsCount || 1} alerts</td>
                                                <td className="py-3 px-4 font-mono text-[11px] text-slate-500">
                                                    {inc.createdDateTime ? new Date(inc.createdDateTime).toLocaleString() : "Recent"}
                                                </td>
                                                <td className="py-3 px-4 text-right">
                                                    <button
                                                        onClick={() => setSelectedItem({ type: "incident", data: inc })}
                                                        className="px-2.5 py-1 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 border border-purple-500/30 rounded text-xs font-semibold transition-colors"
                                                    >
                                                        Inspect
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-12 rounded-xl bg-slate-900/40 border border-white/[0.06] text-center space-y-3">
                                <Layers className="w-10 h-10 text-slate-600 mx-auto" />
                                <div className="text-white font-semibold">No Multi-Stage Incidents</div>
                                <p className="text-xs text-slate-500 max-w-md mx-auto">
                                    0 multi-stage incident correlations currently open in Microsoft 365 Defender.
                                </p>
                            </div>
                        )
                    )}


                    {/* 5️⃣ Read-Only SOAR Actions View */}
                    {activeTab === "actions" && (
                        <div className="w-full space-y-4">
                            <div className="w-full p-4 rounded-xl bg-slate-900/60 border border-white/[0.06] text-xs text-slate-400">
                                Autonomous playbooks and enforcement rules. In accordance with your security directives, all Microsoft Defender connectors operate in <strong>Strict Read-Only Mode</strong>.
                            </div>

                            <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                <div className="p-5 rounded-xl bg-slate-900/70 border border-white/[0.06] space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-white">Live Alert & Threat Telemetry Ingestion</h4>
                                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                            ACTIVE
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-400">
                                        Polls Microsoft Graph Security API every 2 minutes for new security alerts, MITRE tactics, and affected users.
                                    </p>
                                    <div className="text-[11px] text-slate-500 font-mono">Execution Mode: Read-Only Background Stream</div>
                                </div>

                                <div className="p-5 rounded-xl bg-slate-900/70 border border-white/[0.06] space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-white">Machine Health & Vulnerability Exposure</h4>
                                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                            ACTIVE
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-400">
                                        Audits Windows/Linux endpoint sensor health and exposure levels alongside ThreatDown and FortiGate assets.
                                    </p>
                                    <div className="text-[11px] text-slate-500 font-mono">Execution Mode: Continuous Read Audit</div>
                                </div>

                                <div className="p-5 rounded-xl bg-slate-900/70 border border-white/[0.06] space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-white">Endpoint Network Isolation</h4>
                                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-700 text-slate-300 border border-slate-600">
                                            RESTRICTED (READ-ONLY POLICY)
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-400">
                                        Isolating machines via Defender for Endpoint API is disabled by policy. Sentinel operates exclusively in read-only telemetry ingestion.
                                    </p>
                                    <div className="text-[11px] text-slate-500 font-mono">Policy: Write Actions Blocked</div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* ── Inspection Modal ── */}
            {selectedItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-white/[0.1] rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 space-y-5 shadow-2xl">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-base font-bold text-white">{selectedItem.data.title}</h3>
                                <p className="text-xs text-slate-500 font-mono mt-0.5">Alert ID: {selectedItem.data.id}</p>
                            </div>
                            <button
                                onClick={() => setSelectedItem(null)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.04]"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-3 text-xs text-slate-300">
                            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                                <span className="text-slate-500 block text-[10px] uppercase">Description</span>
                                <p className="mt-1">{selectedItem.data.description || "No extended description provided by detection source."}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                                    <span className="text-slate-500 block text-[10px] uppercase">Affected Device</span>
                                    <span className="font-mono text-sky-400">{selectedItem.data.deviceName}</span>
                                </div>
                                <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                                    <span className="text-slate-500 block text-[10px] uppercase">Category</span>
                                    <span>{selectedItem.data.category}</span>
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-white/[0.06] flex justify-end">
                            <button
                                onClick={() => setSelectedItem(null)}
                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
